import { Controller } from '@hotwired/stimulus'
import { Modal } from 'tailwindcss-stimulus-components'

export default class extends Controller {
  static outlets = ['modal']
  static values = { clearTurboFrame: Boolean }

  declare readonly hasModalOutlet: boolean
  declare readonly modalOutlet: Modal
  declare readonly clearTurboFrameValue: boolean

  private titleObserver?: MutationObserver
  private currentTitle?: HTMLElement
  private focusTimeoutId?: number

  private readonly onDialogClose = (): void => {
    this.stopWatching()
  }

  private readonly onFrameLoad = (): void => {
    this.applyTitle(true)
  }

  open (event: Event): void {
    this.stopWatching()

    const frameId = this.element.getAttribute('data-turbo-frame')
    const frame = frameId === null
      ? null
      : this.modalOutlet.element.querySelector(`#${frameId}`)

    if (this.clearTurboFrameValue) frame?.replaceChildren()

    const modal = this.modalOutlet.element
    this.titleObserver = new MutationObserver(() => { this.applyTitle(false) })
    this.titleObserver.observe(modal, { childList: true, subtree: true })
    modal.addEventListener('turbo:frame-load', this.onFrameLoad)
    this.modalOutlet.dialogTarget.addEventListener('close', this.onDialogClose)

    this.modalOutlet.open(event)

    if (frame === null) this.applyTitle(true)
  }

  disconnect (): void {
    this.stopWatching()
  }

  private stopWatching (): void {
    this.titleObserver?.disconnect()
    this.titleObserver = undefined
    this.currentTitle = undefined
    this.clearScheduledFocus()

    if (this.hasModalOutlet) {
      this.modalOutlet.element.removeEventListener('turbo:frame-load', this.onFrameLoad)
      this.modalOutlet.dialogTarget.removeEventListener('close', this.onDialogClose)
    }
  }

  private clearScheduledFocus (): void {
    if (this.focusTimeoutId !== undefined) {
      clearTimeout(this.focusTimeoutId)
      this.focusTimeoutId = undefined
    }
  }

  private applyTitle (force: boolean): void {
    const title = this.modalOutlet.element.querySelector<HTMLElement>('.modal-header h2')
    if (title === null) return
    if (!force && title === this.currentTitle) return

    this.currentTitle = title
    this.modalOutlet.dialogTarget.setAttribute('aria-label', title.textContent?.trim() ?? '')
    this.scheduleFocus(title)
  }

  private scheduleFocus (title: HTMLElement): void {
    this.clearScheduledFocus()

    const focus = (): void => {
      if (title.isConnected) title.focus({ preventScroll: true })
    }

    focus()
    requestAnimationFrame(focus)
    this.focusTimeoutId = window.setTimeout(focus, 0)
  }
}
